import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  Character,
  CreatureInstance,
  CustomParticipant,
  Encounter, 
  // EncounterLog, 
  EncounterParticipant,
} from "@dndModels";

import API from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";

import InitiativeTrackerParticipant from "@dndComponents/encounter/InitiativeTrackerParticipant";
import ButtonBox from "@components/input/buttons/ButtonBox";
import ColorButtonBox from "@components/input/buttons/ColorButtonBox";
import ProgressBar from "@components/input/ProgressBar";
// import LoadingBox from '../../../../components/input/LoadingBox';
import CheckBox from "@components/input/CheckBox";

interface Props { 
  obj: Encounter;
  change_time: number;
  calculate_time: number;
  onChange: (recalc: boolean) => void;
  onSelect: (selected_participant_id: string, selected_ci_id?: string) => void;
  height: number;
}

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();

export default function InitiativeTracker({
  obj,
  change_time,
  calculate_time,
  onChange,
  onSelect,
  height
}: Props) {
  const [prev_change_time, setChangeTime] = useState(change_time);
  const [fetched, setFetched] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [selected_participant_id, setSelectedParticipantId] = useState("");
  const [selected_ci_id, setSelectedCIId] = useState("");
  const [prev_calculate_time, setCalculateTime] = useState(calculate_time);
  const [difficulties_by_color, setDifficultiesByColor] = useState<{ [color: string]: { xp: number, opposing_xps: number, thresholds: { easy: number, medium: number, hard: number, deadly: number }} }>({});
  const [color_for_difficulty, setColorForDifficulty] = useState("");
  const [topHeight, setTopHeight] = useState(0);
    
  const heightFinder: any = React.createRef();

  // eslint-disable-next-line
  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      calc_difficulties();
    }
    // Update height on window resize
    window.addEventListener('resize', updateHeight);

    // Clean up the event listener
    return () => window.removeEventListener('resize', updateHeight);
  });

  useEffect(() => {
    if (change_time !== prev_change_time) {
      setChangeTime(change_time);
    }
  }, [change_time, prev_change_time, setChangeTime]);
  
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  useEffect(() => {
    if (calculate_time !== prev_calculate_time) {
      setCalculateTime(calculate_time);
      calc_difficulties();
    }
    // eslint-disable-next-line
  }, [calculate_time, prev_calculate_time, setCalculateTime]);

  const updateEncounter = (onFinished: Function | null = null) => {
    onChange(true);
    if (onFinished) {
      onFinished();
    }
  } 

  const nextTurn = (participants: EncounterParticipant[], onFinished: Function | null = null) => {
    let current_turn_participant = participants.find(o => o.participant_id === obj.current_turn_participant_id);
    if (current_turn_participant && current_turn_participant.initiative) {
      const initiative = current_turn_participant.initiative;
      const add_time = current_turn_participant.add_time;
      // debugger;
      const later_participants = participants.filter(o => o.initiative !== null && (o.initiative < initiative || (o.initiative === initiative && o.add_time > add_time))).sort((a, b) => { 
        if (a.initiative === null || b.initiative === null) {
          return -1;
        } else if (a.initiative === b.initiative) {
          return +a.add_time > +b.add_time ? 1 : -1;
        } else {
          return +a.initiative < +b.initiative ? 1 : -1;
        }
      });
      if (later_participants.length > 0) {
        obj.current_turn_participant_id = later_participants[0].participant_id;
      } else {
        obj.current_turn_participant_id = participants[0].participant_id;
      }
      // const logs: EncounterLog[] = [];
      // const log = new EncounterLog();
      // log.field = "current_turn_participant_id";
      // log.value = obj.current_turn_participant_id;
      // logs.push(log);
      current_turn_participant = participants.filter(o => o.participant_id === obj.current_turn_participant_id)[0];
      updateEncounter(onFinished);
    } else {
      current_turn_participant = participants.find(o => 
        o.initiative !== null
      );
    }
  }

  const updateHeight = () => {
    if (heightFinder.current instanceof HTMLElement) {
      setTopHeight(heightFinder.current.clientHeight + 10);
    }
  }

  const calc_difficulties = () => {
    let new_color_for_difficulty = color_for_difficulty;
    const new_difficulties_by_color: { [color: string]: { xp: number, opposing_xps: number, thresholds: { easy: number, medium: number, hard: number, deadly: number }} } = {};
    const encounter = obj; // .clone();
    const colors = common_util.unique_array(encounter.participants.filter(o => o.include_in_difficulty).map(o => o.color));
    for (const color of colors) {
      let xp = 0; 
      const thresholds: { easy: number, medium: number, hard: number, deadly: number } = {
        easy: 0,
        medium: 0,
        hard: 0, 
        deadly: 0
      };
      for (const part of encounter.participants.filter(o => o.include_in_difficulty && o.color === color)) {
        if (new_color_for_difficulty === "" && part.participant instanceof Character) {
          new_color_for_difficulty = part.color;
        }
        let level = part.participant instanceof Character ? part.participant.character_level : part.participant?.challenge_rating??0;
        if (part.participant instanceof Character && level === 0) {
          // saved_class_info hasn't been updated yet.  Try to figure it out from saved_race_and_class_string
          const str = part.participant.saved_race_and_class_string;
          if (str.length > 0) {
            const pieces = str.split("/");
            for (const class_string of pieces) {
              const pieces2 = class_string.split(" ");
              if (pieces2.length > 0) {
                const level2 = +pieces2[pieces2.length - 1];
                if (!isNaN(level2)) {
                  level += level2;
                }
              }
            }
          }
        }
        const unit_count = part.child_participants.length > 0 ? part.child_participants.length : 1;
        xp += common_util.cr_to_xp_map[level] * unit_count;
        const threshold = level < 1 ? common_util.level_to_xp_thresholds[1] : (level > 20 ? common_util.level_to_xp_thresholds[20] : common_util.level_to_xp_thresholds[level]);
        thresholds.easy += threshold.easy * unit_count;
        thresholds.medium += threshold.medium * unit_count;
        thresholds.hard += threshold.hard * unit_count;
        thresholds.deadly += threshold.deadly * unit_count;
        // if (color === "white" && part.participant instanceof Character) {
        //   console.log(part.participant);
        //   console.log(level);
        //   console.log(unit_count);
        //   console.log(xp);
        //   console.log({...threshold});
        // }
      }
      new_difficulties_by_color[color] = {
        xp, opposing_xps: 0, thresholds
      };
    }
    if (new_color_for_difficulty === "") {
      const part = encounter.participants.find(o => o.include_in_difficulty);
      if (part) {
        new_color_for_difficulty = part.color;
      }
    }
    for (const color of colors) {
      const diff = new_difficulties_by_color[color];
      diff.opposing_xps = colors.filter(o => o !== color).map(o => new_difficulties_by_color[o].xp).reduce((a, b) => a + b, 0);
    }

    setDifficultiesByColor(new_difficulties_by_color);
    setColorForDifficulty(new_color_for_difficulty);
    
    updateHeight();
  }

  const renderDifficulty = () => {
    // const difficuly_for_color = difficulties_by_color[color_for_difficulty];
    return (
      <Grid
        container
        spacing={0}
        direction="row"
        size={{
          xs: 12
        }}>
        <Grid
          size={{
            xs: 8
          }}>
          Difficulty for Team
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <ColorButtonBox 
            color={color_for_difficulty}
            onChange={setColorForDifficulty}
          />
        </Grid>
        { renderDifficultyForColor() }
      </Grid>
    );
  }

  const renderDifficultyForColor = () => {
    const difficuly_for_color = difficulties_by_color[color_for_difficulty];
    if (difficuly_for_color) {
      const thresholds = difficuly_for_color.thresholds;
      const opposing_xps = difficuly_for_color.opposing_xps;
      let difficulty = "Super Easy";
      let diff_color = "Grey";
      if (opposing_xps > thresholds.easy) {
        if (opposing_xps > thresholds.medium) {
          if (opposing_xps > thresholds.hard) {
            if (opposing_xps > thresholds.deadly) {
              difficulty = "Deadly";
              diff_color = "Red";
            } else {
              difficulty = "Hard";
              diff_color = "DarkRed";
            }
          } else {
            difficulty = "Medium";
            diff_color = "Blue";
          }
        } else {
          difficulty = "Easy";
          diff_color = "Green";
        }
      }
      return (
        <>
          <Grid
            size={{
              xs: 12
            }}>
            <span style={{ color: diff_color }}>Difficulty For Color: { difficulty }, Exp: { opposing_xps }/{difficuly_for_color.thresholds.deadly}</span>
          </Grid>
          <Grid
            size={{
              xs: 12
            }}>
            <ProgressBar color={diff_color} max={difficuly_for_color.thresholds.deadly} value={opposing_xps} />
          </Grid>
        </>
      );
    } else {
      return (
        <>
          <Grid
            size={{
              xs: 12
            }}>
            <span>&nbsp;</span>
          </Grid>
          <Grid
            size={{
              xs: 12
            }}>
          <ProgressBar color="Grey" max={10} value={10} />
          </Grid>
        </>
      );
    }
  }
  
  const owner = api.user !== null && api.user._id === obj.owner_id;
  let participants = owner ? obj.participants : obj.participants.filter(o => !o.hide);
  if (obj.running) {
    participants = participants.sort((a, b) => { 
      if (a.initiative === null || b.initiative === null) {
        if (a.initiative !== null) {
          return -1;
        } else if (b.initiative !== null) {
          return 1;
        } else {
          if (a.include_in_difficulty && !b.include_in_difficulty) {
            return -1;
          } else if (!a.include_in_difficulty && b.include_in_difficulty) {
            return 1;
          } else {
            return 0;
          }
        }
      } else if (a.initiative === b.initiative) {
        return +a.add_time > +b.add_time ? 1 : -1;
      } else {
        return +a.initiative < +b.initiative ? 1 : -1;
      }
    });
  }
  // const topHeight = window.innerWidth <= 340 ? 240 : window.innerWidth < 600 ? 220 : window.innerWidth === 600 ? 305 : window.innerWidth < 704 ? 305 : window.innerWidth <= 924 ? 290 : window.innerWidth < 1379 ? 255 : 194; // window.innerWidth < 1242 ? 224.5 : 197;
  return (
    <Grid container spacing={0} direction="row">
      { owner &&
        <Grid
          style={ topHeight === 0 ? {} : { height: topHeight } }
          size={{
            xs: 12
          }}>
          <Grid container spacing={0} direction="row" ref={heightFinder}>
            <Grid
              size={{
                xs: 4
              }}>
              <CheckBox
                name="Chase"
                value={obj.is_chase}
                onChange={(changed: boolean) => {
                  const new_obj = obj; // .clone();
                  new_obj.is_chase = changed;
                  // const log = new EncounterLog();
                  // log.field = "is_chase";
                  // log.value = changed;
                  updateEncounter();
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 4
              }}>
              <CheckBox
                name="Trim"
                value={obj.trim_stat_blocks}
                onChange={(changed: boolean) => {
                  const new_obj = obj; // .clone();
                  new_obj.trim_stat_blocks = changed;
                  // const log = new EncounterLog();
                  // log.field = "trim_stat_blocks";
                  // log.value = changed;
                  updateEncounter();
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 4
              }}>
              <CheckBox
                name="Hide Desc"
                value={obj.hide_description}
                onChange={(changed: boolean) => {
                  // console.log(obj.hide_description);
                  const new_obj = obj; // .clone();
                  new_obj.hide_description = changed;
                  // const log = new EncounterLog();
                  // log.field = "hide_description";
                  // log.value = changed;
                  updateEncounter();
                }}
              />
            </Grid>
            { renderDifficulty() }
            <Grid
              container
              spacing={0}
              direction="row"
              size={{
                xs: 12
              }}>
              <Grid
                size={{
                  xs: obj.current_turn_participant_id === "" ? 6 : 3
                }}>
                <ButtonBox 
                  name={ obj.running ? "Pause" : "Start" }
                  disabled={ participants.filter(o => o.initiative !== null).length === 0 }
                  onClick={() => {
                    const new_obj = obj;
                    new_obj.running = !new_obj.running;
                    // let log2: EncounterLog | null = null;
                    if (new_obj.running) {
                      if (new_obj.current_turn_participant_id === "" && participants.filter(o => o.initiative !== null).length > 0) {
                        new_obj.current_turn_participant_id = participants.filter(o => o.initiative !== null)[0].participant_id;
                        // log2 = new EncounterLog();
                        // log2.field = "current_turn_participant_id";
                        // log2.value = new_obj.current_turn_participant_id;
                      }
                    }
                    // const log = new EncounterLog();
                    // log.field = "running";
                    // log.value = new_obj.running;
                    updateEncounter();
                    setReloading(true);
                  }}
                />
              </Grid>
              { obj.current_turn_participant_id !== "" &&
                <Grid
                  size={{
                    xs: 3
                  }}>
                  <ButtonBox 
                    name="End"
                    onClick={() => {
                      const new_obj = obj;
                      new_obj.running = false;
                      new_obj.current_turn_participant_id = "";
                      new_obj.current_initiative = -10;
                      // const log2 = new EncounterLog();
                      // log2.field = "current_turn_participant_id";
                      // log2.value = "";
                      // const log = new EncounterLog();
                      // log.field = "running";
                      // log.value = new_obj.running;
                      updateEncounter();
                      setReloading(true);
                    }}
                  />
                </Grid>
              }
              <Grid
                size={{
                  xs: 6
                }}>
                <ButtonBox 
                  name="Next Turn"
                  disabled={ !obj.running }
                  onClick={() => {
                    nextTurn(participants);
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 6
                }}>
                <Grid container spacing={0} direction="row">
                  <Grid
                    size={{
                      xs: 12,
                      lg: 6
                    }}>
                    <ButtonBox 
                      name="Roll Missing"
                      disabled={ obj.running || obj.participants.filter(o => !(o.participant instanceof Character) && o.initiative === null && o.include_in_difficulty).length === 0 }
                      onClick={() => {
                        const new_obj = obj;
                        // debugger;
                        for (const participant of new_obj.participants.filter(o => 
                          !(o.participant instanceof Character) && 
                          o.initiative === null && o.include_in_difficulty)) {
                          const adv = participant.initiative_adv;
                          let roll = common_util.get_random(20);
                          if (adv > 0) {
                            roll = Math.max(roll, common_util.get_random(20));
                          } else if (adv < 0) {
                            roll = Math.min(roll, common_util.get_random(20));
                          }
                          // console.log(roll);
                          participant.initiative = Math.max(0, roll + participant.initiative_modifier);
                        }
                        // const log = new EncounterLog();
                        // log.field = "participants";
                        // log.value = "refresh";
                        updateEncounter();
                      }}
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      lg: 6
                    }}>
                    <ButtonBox 
                      name="Use Passives"
                      disabled={ obj.running || obj.participants.filter(o => !(o.participant instanceof Character) && o.initiative === null && o.include_in_difficulty).length === 0 }
                      onClick={() => {
                        const new_obj = obj;
                        // debugger;
                        for (const participant of new_obj.participants.filter(o => 
                          !(o.participant instanceof Character) && 
                          o.initiative === null && o.include_in_difficulty)) {
                          participant.initiative = 10 + participant.initiative_modifier;
                        }
                        // const log = new EncounterLog();
                        // log.field = "participants";
                        // log.value = "refresh";
                        updateEncounter();
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid
                size={{
                  xs: 6
                }}>
                <Grid container spacing={0} direction="row">
                  <Grid
                    size={{
                      xs: 12,
                      lg: 6
                    }}>
                    <ButtonBox 
                      name="Alternating Initiatives"
                      disabled={ obj.running || common_util.unique_array(obj.participants.filter(o => o.initiative !== null && o.participant_type !== "Lair Actions").map(o => o.color)).length < 2 }
                      onClick={() => {
                        const new_obj = obj;
                        const the_participants = new_obj.participants.filter(o => o.initiative !== null && o.participant_type !== "Lair Actions");
                        const colors = common_util.unique_array(the_participants.map(o => o.color));
                        const participants_by_color: { 
                          color: string, 
                          participants: {
                            participant: EncounterParticipant, 
                            new_init_mod: number
                          }[]
                        }[] = colors.map(color => {
                          return { color, participants: the_participants.filter(o => o.color === color).map(participant => {
                            return {
                              participant, new_init_mod: -1
                            };
                          }) };
                        });
                        const color_order = colors.map(color => {
                          return { color, highest: Math.max(...the_participants.filter(o2 => o2.color === color && o2.initiative !== null).map(o2 => {
                            if (o2.initiative === null) {
                              return -1;
                            } else {
                              return o2.initiative;
                            }
                          }))};
                        }).sort((a, b) => {
                          return +a.highest > +b.highest ? -1 : 1;
                        }).map(o => o.color);
                        let color_index = 0;
                        for (let i = 0; i < the_participants.length; ++i) {
                          let color = color_order[color_index];
                          if (!color) {
                            color_index = 0;
                            color = color_order[color_index];
                          }
                          if (color) {
                            let next_participant = participants_by_color.filter(o => 
                              o.color === color)[0].participants.filter(o => o.new_init_mod === null)[0];
                            while (!next_participant) {
                              color_index++;
                              color = color_order[color_index];
                              if (!color) {
                                color_index = 0;
                                color = color_order[color_index];
                              }
                               
                              next_participant = participants_by_color.filter(o => 
                                o.color === color)[0].participants.filter(o => o.new_init_mod === null)[0];
                            }
                            if (next_participant) {
                              next_participant.new_init_mod = i;
                            }
                          }
                          color_index++;
                        }
                        const max = the_participants.length > 25 ? the_participants.length : 25;
                        for (const participant of participants_by_color) {
                          for (const part of participant.participants) {
                            part.participant.initiative = max - part.new_init_mod;
                          }
                        }
                        // const log = new EncounterLog();
                        // log.field = "participants";
                        // log.value = "refresh";
                        updateEncounter();
                      }}
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      lg: 6
                    }}>
                    <ButtonBox 
                      name="Clear Initiatives"
                      disabled={ obj.running || obj.current_turn_participant_id !== "" || obj.participants.filter(o => o.initiative !== null && o.participant_type !== "Lair Actions").length === 0 }
                      onClick={() => {
                        const new_obj = obj;
                        const the_participants = new_obj.participants.filter(o => o.initiative !== null && o.participant_type !== "Lair Actions");
                        for (const part of the_participants) {
                          part.initiative = null;
                        }
                        // const log = new EncounterLog();
                        // log.field = "participants";
                        // log.value = "refresh";
                        updateEncounter();
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      }
      { (!owner || topHeight > 0) &&
        <Grid
          className="padded"
          style={{ border: "1px solid", height: height - topHeight + 4 }}
          size={{
            xs: 12
          }}>
          <Grid container spacing={0} direction="row">
            <Grid
              style={{ height: height - topHeight - 4, overflowY: "scroll" }}
              size={{
                xs: 12
              }}>
              <Grid container spacing={0} direction="row">
                { !reloading && participants.map(participant => {
                  return (
                    <Grid
                      key={participant.participant_id}
                      size={{
                        xs: 12
                      }}>
                      <InitiativeTrackerParticipant
                        obj={participant}
                        encounter={obj}
                        change_time={change_time}
                        selected_participant_id={selected_participant_id}
                        selected_ci_id={selected_ci_id}
                        current_turn_participant_id={obj.current_turn_participant_id}
                        onSelect={(ci: CreatureInstance | CustomParticipant | null) => {
                          // console.log(ci);
                          if (ci) {
                            const new_id = (participant.participant_id === selected_participant_id && ci.true_id === selected_ci_id ? "" : participant.participant_id);
                            setSelectedParticipantId(new_id);
                            setSelectedCIId(new_id === "" ? "" : ci.true_id);
                            onSelect(new_id, new_id === "" ? "" : ci.true_id);
                          } else {
                            const new_id = (participant.participant_id === selected_participant_id ? "" : participant.participant_id);
                            setSelectedParticipantId(new_id);
                            onSelect(new_id);
                          }
                        }}
                        onChange={() => {
                          updateEncounter();
                        }}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      }
    </Grid>
  );
}
